import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Edit,
  Trash2,
  ArrowLeft,
  Clock,
  Calendar,
  CheckCircle2,
  FileText,
  Plus,
  ChevronRight,
  Download,
  AlertCircle,
  BriefcaseIcon,
  Stamp,
  Receipt,
  Building,
  Pencil,
  X,
  Search,
  Save,
  Loader2,
} from "lucide-react";
import Header from "../../Header";
import ApplicationSteps from "./ApplicationSteps";
import KYCStatusSection from "./KYCStatusSection";

const CompanyDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [uploading, setUploading] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState("OTHER");
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  // Activity editing state
  const [editingActivities, setEditingActivities] = useState(false);
  const [availableActivities, setAvailableActivities] = useState([]);
  const [activitySearch, setActivitySearch] = useState("");
  const [editedActivities, setEditedActivities] = useState([]);
  const [savingActivities, setSavingActivities] = useState(false);

  // Company name editing state
  const [editingNames, setEditingNames] = useState(false);
  const [editedNames, setEditedNames] = useState(null);
  const [savingNames, setSavingNames] = useState(false);

  // Add this new function to fetch services
  const fetchServices = async () => {
    try {
      setServicesLoading(true);
      const response = await fetch(
        `https://api.instacorp.ae/api/admin/data/companies/${id}/services`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch services");
      const data = await response.json();
      setServices(data.services);
    } catch (error) {
      console.error("Error fetching services:", error);
      setNotification({
        show: true,
        message: "Failed to load services",
        type: "error",
      });
    } finally {
      setServicesLoading(false);
    }
  };

  const [activeTab, setActiveTab] = useState("overview");
  const fetchCompany = async () => {
    try {
      const response = await fetch(
        `https://api.instacorp.ae/api/admin/data/companies/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch company");
      const data = await response.json();
      setCompany(data);
    } catch (error) {
      console.error("Error fetching company:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "services") {
      fetchServices();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchServices is stable
  }, [activeTab, id]);

  useEffect(() => {
    fetchCompany();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchCompany is stable
  }, [id]);

  const DOCUMENT_TYPES = {
    PASSPORT: {
      label: "Passport Copy",
      description: "Clear copy of passport",
    },
    VISA: {
      label: "Visa Copy",
      description: "Valid UAE visa copy",
    },
    EMIRATES_ID: {
      label: "Emirates ID",
      description: "Front and back of Emirates ID",
    },
    OTHER: {
      label: "Other Documents",
      description: "Any additional documents",
    },
  };

  const handleDocumentUpload = async (file, documentType = "OTHER") => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("documentType", documentType);

    try {
      setUploading(true);
      const response = await fetch(
        `https://api.instacorp.ae/api/admin/data/companies/${id}/documents`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to upload document");
      }

      setNotification({
        show: true,
        message: "Document uploaded successfully",
        type: "success",
      });

      fetchCompany();
    } catch (error) {
      console.error("Error uploading document:", error);
      setNotification({
        show: true,
        message: error.message || "Failed to upload document",
        type: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentDelete = async (fileUrl, filename) => {
    // Show confirmation dialog
    if (
      !window.confirm(
        `Are you sure you want to delete "${filename}"? This action cannot be undone.`
      )
    ) {
      return;
    }
  
    try {
      // Ensure fileUrl is a string - if it's accidentally an object, extract the fileUrl property
      const urlToDelete = typeof fileUrl === 'object' && fileUrl.fileUrl 
        ? fileUrl.fileUrl 
        : fileUrl;
      
      // Encode the URL to ensure it's properly transmitted in the request
      const encodedFileUrl = encodeURIComponent(urlToDelete);
      
      const response = await fetch(
        `https://api.instacorp.ae/api/admin/data/companies/${id}/documents/${encodedFileUrl}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete document");
      }
  
      setNotification({
        show: true,
        message: "Document deleted successfully",
        type: "success",
      });
  
      // Refresh company data to get updated documents list
      fetchCompany();
    } catch (error) {
      console.error("Error deleting document:", error);
      setNotification({
        show: true,
        message: error.message || "Failed to delete document",
        type: "error",
      });
    }
  };

  const handleStepUpdate = async (stepKey, status) => {
    try {
      const response = await fetch(
        `https://api.instacorp.ae/api/admin/data/companies/${id}/steps/${stepKey}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ status }),
        }
      );

      if (!response.ok) throw new Error("Failed to update step");

      // Refresh company data to get updated steps
      fetchCompany();
    } catch (error) {
      console.error("Error updating step:", error);
    }
  };

  // --- Activity editing handlers ---
  const handleStartEditActivities = async () => {
    setEditedActivities([...(company?.companyActivities || [])]);
    setEditingActivities(true);
    setActivitySearch("");
    try {
      const response = await fetch(
        "https://api.instacorp.ae/api/ai/all/activities",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ companyId: id }),
        }
      );
      if (!response.ok) throw new Error("Failed to fetch activities");
      const data = await response.json();
      setAvailableActivities(
        Array.isArray(data) ? data : data.activities || []
      );
    } catch (error) {
      console.error("Error fetching activities:", error);
      setNotification({
        show: true,
        message: "Failed to load available activities",
        type: "error",
      });
    }
  };

  const handleSaveActivities = async () => {
    try {
      setSavingActivities(true);
      const response = await fetch(
        `https://api.instacorp.ae/api/admin/data/companies/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ companyActivities: editedActivities }),
        }
      );
      if (!response.ok) throw new Error("Failed to update activities");
      setNotification({
        show: true,
        message: "Activities updated successfully",
        type: "success",
      });
      setEditingActivities(false);
      fetchCompany();
    } catch (error) {
      console.error("Error saving activities:", error);
      setNotification({
        show: true,
        message: error.message || "Failed to update activities",
        type: "error",
      });
    } finally {
      setSavingActivities(false);
    }
  };

  const handleRemoveActivity = (index) => {
    setEditedActivities((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddActivity = (activity) => {
    const alreadyAdded = editedActivities.some(
      (a) =>
        (a["Activity Code"] || a.code) ===
        (activity["Activity Code"] || activity.code)
    );
    if (!alreadyAdded) {
      setEditedActivities((prev) => [...prev, activity]);
    }
    setActivitySearch("");
  };

  // --- Company name editing handlers ---
  const handleStartEditNames = () => {
    const names = company?.companyNames || {};
    setEditedNames({
      first: {
        english: names.first?.english || "",
        arabic: names.first?.arabic || "",
      },
      second: {
        english: names.second?.english || "",
        arabic: names.second?.arabic || "",
      },
      third: {
        english: names.third?.english || "",
        arabic: names.third?.arabic || "",
      },
    });
    setEditingNames(true);
  };

  const handleSaveNames = async () => {
    try {
      setSavingNames(true);
      const response = await fetch(
        `https://api.instacorp.ae/api/admin/data/companies/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ companyNames: editedNames }),
        }
      );
      if (!response.ok) throw new Error("Failed to update company names");
      setNotification({
        show: true,
        message: "Company names updated successfully",
        type: "success",
      });
      setEditingNames(false);
      fetchCompany();
    } catch (error) {
      console.error("Error saving company names:", error);
      setNotification({
        show: true,
        message: error.message || "Failed to update company names",
        type: "error",
      });
    } finally {
      setSavingNames(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      processing: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      pending: "bg-blue-100 text-blue-800",
      rejected: "bg-red-100 text-red-800",
      imported: "bg-purple-100 text-purple-800",
    };
    return statusColors[status] || "bg-gray-100 text-gray-800";
  };

  const formatDocumentName = (key) => {
    return key
      .replace(/Url$/, "")
      .split(/(?=[A-Z])/)
      .join(" ")
      .replace(/^\w/, (c) => c.toUpperCase());
  };

  /** Normalize a single document entry (array item or object value) to { fileUrl, originalName, uploadDate } */
  const normalizeDocEntry = (entry, fallbackName = "Document") => {
    if (!entry) return null;
    if (typeof entry === "string")
      return { fileUrl: entry, originalName: fallbackName, uploadDate: null };
    const url = entry.fileUrl || entry.url;
    if (!url) return null;
    return {
      fileUrl: url,
      originalName:
        entry.originalName ||
        (entry.documentType &&
          String(entry.documentType).replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())) ||
        fallbackName,
      uploadDate: entry.uploadDate ?? entry.uploadedAt ?? null,
    };
  };

  /** Get shareholder documents (passport + photo per shareholder) for display */
  const getShareholderDocuments = (company) => {
    const shareholders = company?.shareholderDetails?.shareholders || [];
    const list = [];
    shareholders.forEach((sh, index) => {
      const name = sh.name || `Shareholder ${index + 1}`;
      const docs = sh.documents || {};
      if (docs.passportCopy) {
        const url = typeof docs.passportCopy === "string" ? docs.passportCopy : docs.passportCopy?.fileUrl;
        if (url)
          list.push({
            fileUrl: url,
            originalName: `${name} – Passport Copy`,
            uploadDate: typeof docs.passportCopy === "object" ? docs.passportCopy?.uploadDate : null,
          });
      }
      if (docs.coloredPhoto) {
        const url = typeof docs.coloredPhoto === "string" ? docs.coloredPhoto : docs.coloredPhoto?.fileUrl;
        if (url)
          list.push({
            fileUrl: url,
            originalName: `${name} – Colored Photo`,
            uploadDate: typeof docs.coloredPhoto === "object" ? docs.coloredPhoto?.uploadDate : null,
          });
      }
    });
    return list;
  };

  /** Get company-level documents (flat array), normalized and optionally excluding URLs already in shareholder docs */
  const getOtherDocuments = (company, excludeUrlsSet) => {
    if (company?.status === "imported") {
      return Object.entries(company.documents || {}).map(([key, url]) => ({
        originalName: formatDocumentName(key),
        fileUrl: url,
        uploadDate: company.dateCreated,
      }));
    }
    const raw = company?.documents;
    if (!Array.isArray(raw)) return [];
    const other = [];
    for (const entry of raw) {
      const doc = normalizeDocEntry(entry, "Document");
      if (!doc || (excludeUrlsSet && excludeUrlsSet.has(doc.fileUrl))) continue;
      other.push(doc);
    }
    return other;
  };

  /** Combined documents: shareholder docs first, then other. Used for count and for single list if needed. */
  const normalizeDocuments = (company) => {
    const shareholderDocs = getShareholderDocuments(company);
    const shareholderUrls = new Set(shareholderDocs.map((d) => d.fileUrl));
    const otherDocs = getOtherDocuments(company, shareholderUrls);
    return [...shareholderDocs, ...otherDocs];
  };

  const TabButton = ({ tab, icon: Icon, label, count }) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
        activeTab === tab
          ? "bg-indigo-50 text-indigo-700"
          : "text-gray-600 hover:bg-gray-50"
      }`}
    >
      <Icon className="w-5 h-5 mr-2" />
      {label}
      {count !== undefined && count > 0 && (
        <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
          {count}
        </span>
      )}
    </button>
  );

  const ActivityItem = ({ icon: Icon, title, description, date }) => (
    <div className="flex space-x-3 mb-4">
      <div className="flex-shrink-0">
        <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center">
          <Icon className="w-4 h-4 text-indigo-600" />
        </div>
      </div>
      <div className="flex-grow">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        <p className="text-sm text-gray-500">{description}</p>
        <p className="text-xs text-gray-400 mt-1">
          {new Date(date).toLocaleDateString()}
        </p>
      </div>
    </div>
  );

  const renderDocumentRow = (doc, key) => (
    <li key={key} className="p-4 hover:bg-gray-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center min-w-0">
          <FileText className="h-8 w-8 text-gray-400 flex-shrink-0" />
          <div className="ml-4 min-w-0">
            <p className="font-medium text-gray-900 truncate">
              {doc.originalName}
            </p>
            <div className="mt-1">
              <span className="text-sm text-gray-500">
                {doc.uploadDate
                  ? new Date(doc.uploadDate).toLocaleDateString()
                  : "—"}
              </span>
            </div>
          </div>
        </div>
        <div className="flex space-x-2 flex-shrink-0">
          <a
            href={doc.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-gray-400 hover:text-gray-500"
          >
            <Download className="w-5 h-5" />
          </a>
          <button
            onClick={() =>
              handleDocumentDelete(doc.fileUrl, doc.originalName)
            }
            className="p-2 text-red-400 hover:text-red-500"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </li>
  );

  const renderDocumentsSection = () => {
    const shareholderDocs = getShareholderDocuments(company);
    const shareholderUrls = new Set(shareholderDocs.map((d) => d.fileUrl));
    const otherDocs = getOtherDocuments(company, shareholderUrls);
    const totalCount = shareholderDocs.length + otherDocs.length;

    return (
      <div className="space-y-6">
        {notification.show && (
          <div
            className={`p-4 rounded-md ${
              notification.type === "success"
                ? "bg-green-50 text-green-800"
                : "bg-red-50 text-red-800"
            }`}
          >
            {notification.message}
          </div>
        )}

        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Documents</h3>
          <button
            onClick={() => setShowUploadForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Upload Document
          </button>
        </div>

        {showUploadForm && (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-end space-x-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Document Type
                </label>
                <select
                  value={selectedDocumentType}
                  onChange={(e) => setSelectedDocumentType(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  {Object.entries(DOCUMENT_TYPES).map(([key, { label }]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {DOCUMENT_TYPES[selectedDocumentType].description}
                </p>
              </div>
              <div className="flex-1">
                <input
                  id="fileUpload"
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    handleDocumentUpload(
                      e.target.files[0],
                      selectedDocumentType
                    );
                    setShowUploadForm(false);
                  }}
                  disabled={uploading}
                />
                <button
                  onClick={() => document.getElementById("fileUpload").click()}
                  disabled={uploading}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                      Uploading...
                    </>
                  ) : (
                    "Choose File"
                  )}
                </button>
                <button
                  onClick={() => setShowUploadForm(false)}
                  className="ml-2 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {totalCount === 0 ? (
          <div className="bg-white shadow overflow-hidden rounded-md">
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No documents
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                No documents available
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {shareholderDocs.length > 0 && (
              <div className="bg-white shadow overflow-hidden rounded-md">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Shareholder documents
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Passport copy and colored photo per shareholder
                  </p>
                </div>
                <ul className="divide-y divide-gray-200">
                  {shareholderDocs.map((doc, index) =>
                    renderDocumentRow(doc, `sh-${index}`)
                  )}
                </ul>
              </div>
            )}
            {otherDocs.length > 0 && (
              <div className="bg-white shadow overflow-hidden rounded-md">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h4 className="text-sm font-semibold text-gray-900">
                    {company?.status === "imported"
                      ? "Company documents"
                      : "Other documents"}
                  </h4>
                </div>
                <ul className="divide-y divide-gray-200">
                  {otherDocs.map((doc, index) =>
                    renderDocumentRow(doc, `other-${index}`)
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  /** Safely extract a displayable string from a value that might be a plain string or an object with a `name` key */
  const safeStr = (val) => {
    if (val == null) return "";
    if (typeof val === "string") return val;
    if (typeof val === "object" && val.name) return val.name;
    return String(val);
  };

  /** Helper to display a license fee value.
   *  Handles: number, array [val0, val1, ...], or object {"1": val, "2": val, ...} */
  const formatLicenseFee = (fee, multiyear) => {
    if (fee == null) return "—";
    const year = multiyear || 1;
    let val;
    if (typeof fee === "number") {
      val = fee;
    } else if (Array.isArray(fee)) {
      val = fee[year - 1] ?? fee[0];
    } else if (typeof fee === "object") {
      // Object keyed by year: {"1": 10075, "2": 20150, ...}
      val = fee[String(year)] ?? fee[Object.keys(fee)[0]];
    }
    return val != null ? `AED ${Number(val).toLocaleString()}` : "—";
  };

  /** Extract a viewable URL from a document field (handles string or {url, fileUrl} object) */
  const getDocUrl = (doc) => {
    if (!doc) return null;
    if (typeof doc === "string") return doc;
    return doc.url || doc.fileUrl || null;
  };

  /** Render a person card (shareholder, secretary, GM, director) */
  const renderPersonCard = (person, options = {}) => {
    const { showShares = false, showKYC = false, showResidency = false } = options;
    if (!person) return null;

    // Collect all document links for this person
    const docs = person.documents || {};
    const docEntries = [
      { label: "Passport Copy", url: getDocUrl(docs.passportCopy) },
      { label: "Colored Photo", url: getDocUrl(docs.coloredPhoto) },
      { label: "Emirates ID", url: getDocUrl(docs.emiratesId || docs.eidCopy) },
      { label: "Visa Copy", url: getDocUrl(docs.visaCopy) },
      { label: "CV", url: getDocUrl(docs.cv) },
      { label: "Professional Background", url: getDocUrl(docs.professionalBackground) },
    ].filter((d) => d.url);

    return (
      <div className="p-4 border rounded-lg space-y-4">
        {/* Name & Residency */}
        <div className="flex justify-between items-start">
          <div>
            <p className="font-medium text-gray-900">{person.name}</p>
            {person.fatherName && (
              <p className="text-sm text-gray-500 mt-1">
                Father's Name: {person.fatherName}
              </p>
            )}
            {person.motherName && (
              <p className="text-sm text-gray-500 mt-0.5">
                Mother's Name: {person.motherName}
              </p>
            )}
          </div>
          {showResidency && (
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                person.isUAEresident || person.isUaeResident
                  ? "bg-green-100 text-green-800"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {person.isUAEresident || person.isUaeResident
                ? "UAE Resident"
                : "Non-Resident"}
            </span>
          )}
        </div>

        {/* Detail Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {person.nationality && (
            <div>
              <p className="text-sm text-gray-500">Nationality</p>
              <p className="text-sm text-gray-900">{person.nationality}</p>
            </div>
          )}
          {showShares && person.shares != null && (
            <div>
              <p className="text-sm text-gray-500">Shares</p>
              <p className="text-sm text-gray-900">{person.shares} units</p>
            </div>
          )}
          {person.email && (
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="text-sm text-gray-900 break-all">{person.email}</p>
            </div>
          )}
          {person.mobile && (
            <div>
              <p className="text-sm text-gray-500">Mobile</p>
              <p className="text-sm text-gray-900">{person.mobile}</p>
            </div>
          )}
          {person.passportNumber && (
            <div>
              <p className="text-sm text-gray-500">Passport No.</p>
              <p className="text-sm text-gray-900">{person.passportNumber}</p>
            </div>
          )}
          {(person.emiratesId && typeof person.emiratesId === "string") && (
            <div>
              <p className="text-sm text-gray-500">Emirates ID</p>
              <p className="text-sm text-gray-900">{person.emiratesId}</p>
            </div>
          )}
          {person.applicantId && (
            <div>
              <p className="text-sm text-gray-500">Onfido Applicant ID</p>
              <p className="text-sm text-gray-900 font-mono text-xs break-all">
                {person.applicantId}
              </p>
            </div>
          )}
        </div>

        {/* Address */}
        {(person.addressLine1 || person.cityState || person.country) && (
          <div>
            <p className="text-sm text-gray-500">Address</p>
            <p className="text-sm text-gray-900">
              {[person.addressLine1, person.addressLine2].filter(Boolean).join(", ")}
            </p>
            <p className="text-sm text-gray-900">
              {[person.cityState, person.country, person.postalCode].filter(Boolean).join(", ")}
            </p>
          </div>
        )}

        {/* Inline Documents */}
        {docEntries.length > 0 && (
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-gray-900 mb-2">Documents</p>
            <div className="flex flex-wrap gap-2">
              {docEntries.map((doc, idx) => (
                <a
                  key={idx}
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
                >
                  <FileText className="h-3.5 w-3.5" />
                  {doc.label}
                  <Download className="h-3 w-3" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* KYC Status */}
        {showKYC && person.onfidoWorkflowId && (
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              KYC Verification Status
            </h4>
            <KYCStatusSection shareholder={person} companyId={id} />
          </div>
        )}
      </div>
    );
  };

  const renderOverviewContent = () => {
    const isImported = company?.status === "imported";
    const pkg = company?.package;
    const details = company?.companyDetails;
    const names = company?.companyNames;
    const shDetails = company?.shareholderDetails;

    return (
      <div className="space-y-6">
        {/* Status & Dates Bar */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="px-6 py-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Status</p>
                <span
                  className={`mt-1 inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                    company?.status
                  )}`}
                >
                  {company?.status}
                </span>
              </div>
              {company?.stage && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Current Stage</p>
                  <p className="text-sm text-gray-900 mt-1 capitalize">
                    {company.stage.replace(/([A-Z])/g, " $1").trim()}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-gray-500">Active</p>
                <span
                  className={`mt-1 inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    company?.isActive
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {company?.isActive ? "Yes" : "No"}
                </span>
              </div>
              {company?.dateCreated && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Created</p>
                  <p className="text-sm text-gray-900 mt-1">
                    {new Date(company.dateCreated).toLocaleDateString()}
                  </p>
                </div>
              )}
              {company?.lastUpdated && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Last Updated</p>
                  <p className="text-sm text-gray-900 mt-1">
                    {new Date(company.lastUpdated).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-medium text-gray-900">
              Company Information
            </h3>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {company?.companyType && (
                <div className="flex items-start space-x-3">
                  <Building2 className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Business Description
                    </p>
                    <p className="text-sm text-gray-900 capitalize">
                      {safeStr(company.companyType)}
                    </p>
                  </div>
                </div>
              )}
              {details?.typeOfCompany && (
                <div className="flex items-start space-x-3">
                  <Building2 className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Company Type
                    </p>
                    <p className="text-sm text-gray-900">
                      {safeStr(details.typeOfCompany)}
                    </p>
                  </div>
                </div>
              )}
              {company?.licenseType && (
                <div className="flex items-start space-x-3">
                  <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      License Type
                    </p>
                    <p className="text-sm text-gray-900 capitalize">
                      {safeStr(company.licenseType)}
                    </p>
                  </div>
                </div>
              )}
              {company?.typeOfOffice && (
                <div className="flex items-start space-x-3">
                  <Building2 className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Office Type
                    </p>
                    <p className="text-sm text-gray-900">
                      {safeStr(company.typeOfOffice)}
                    </p>
                  </div>
                </div>
              )}
              {!isImported && details?.typeOfTradeZone && (
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Jurisdiction
                    </p>
                    <p className="text-sm text-gray-900">
                      {safeStr(details.typeOfTradeZone)} — {safeStr(details.location)}
                    </p>
                  </div>
                </div>
              )}
              {!isImported && details?.timeline && (
                <div className="flex items-start space-x-3">
                  <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Timeline
                    </p>
                    <p className="text-sm text-gray-900">{safeStr(details.timeline)}</p>
                  </div>
                </div>
              )}
              {company?.email && (
                <div className="flex items-start space-x-3">
                  <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Email</p>
                    <p className="text-sm text-gray-900 break-all">
                      {company.email}
                    </p>
                  </div>
                </div>
              )}
              {company?.phone && (
                <div className="flex items-start space-x-3">
                  <Phone className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Phone</p>
                    <p className="text-sm text-gray-900">{company.phone}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Company Names */}
        {names && (names.first || names.second || names.third) && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Company Names
              </h3>
              {!editingNames && (
                <button
                  onClick={handleStartEditNames}
                  className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                  title="Edit company names"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="px-6 py-4">
              {editingNames && editedNames ? (
                <div className="space-y-4">
                  {[
                    { key: "first", label: "First Choice" },
                    { key: "second", label: "Second Choice" },
                    { key: "third", label: "Third Choice" },
                  ].map((n) => (
                    <div key={n.key} className="p-4 border rounded-lg space-y-3">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {n.label}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            English
                          </label>
                          <input
                            type="text"
                            value={editedNames[n.key]?.english || ""}
                            onChange={(e) =>
                              setEditedNames((prev) => ({
                                ...prev,
                                [n.key]: {
                                  ...prev[n.key],
                                  english: e.target.value,
                                },
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            Arabic
                          </label>
                          <input
                            type="text"
                            dir="rtl"
                            value={editedNames[n.key]?.arabic || ""}
                            onChange={(e) =>
                              setEditedNames((prev) => ({
                                ...prev,
                                [n.key]: {
                                  ...prev[n.key],
                                  arabic: e.target.value,
                                },
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end space-x-3 pt-2">
                    <button
                      onClick={() => setEditingNames(false)}
                      disabled={savingNames}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveNames}
                      disabled={savingNames}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400"
                    >
                      {savingNames ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Names
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {[
                    { label: "First Choice", data: names.first },
                    { label: "Second Choice", data: names.second },
                    { label: "Third Choice", data: names.third },
                  ]
                    .filter((n) => n.data?.english || n.data?.arabic)
                    .map((n, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div className="space-y-1 flex-1">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            {n.label}
                          </p>
                          <p className="font-medium text-gray-900">
                            {n.data.english}
                          </p>
                        </div>
                        {n.data.arabic && (
                          <p className="font-medium text-gray-900 text-right" dir="rtl">
                            {n.data.arabic}
                          </p>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}

        {!isImported && (
          <>
            {/* Business Activities */}
            {(company?.companyActivities?.length > 0 || editingActivities) && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    Business Activities
                  </h3>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-500">
                      {editingActivities
                        ? `${editedActivities.length} activit${editedActivities.length === 1 ? "y" : "ies"}`
                        : `${company.companyActivities.length} activit${company.companyActivities.length === 1 ? "y" : "ies"}`}
                    </span>
                    {!editingActivities && (
                      <button
                        onClick={handleStartEditActivities}
                        className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                        title="Edit activities"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="px-6 py-4">
                  {editingActivities ? (
                    <div className="space-y-4">
                      {/* Current activities with remove buttons */}
                      <div className="space-y-2">
                        {editedActivities.map((activity, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 border rounded-lg bg-gray-50"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900 text-sm">
                                {activity["Activity Name"] || activity.name}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {[
                                  activity["Activity Code"] || activity.code
                                    ? `Code: ${activity["Activity Code"] || activity.code}`
                                    : null,
                                  activity.Classification || activity.classification,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </p>
                            </div>
                            <button
                              onClick={() => handleRemoveActivity(index)}
                              className="ml-2 p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                              title="Remove activity"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        {editedActivities.length === 0 && (
                          <p className="text-sm text-gray-500 italic py-2">
                            No activities selected. Use the search below to add activities.
                          </p>
                        )}
                      </div>

                      {/* Search and add new activities */}
                      <div className="relative">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={activitySearch}
                            onChange={(e) => setActivitySearch(e.target.value)}
                            placeholder="Search activities to add..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                        {activitySearch.length >= 2 && (
                          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {availableActivities
                              .filter((a) => {
                                const name = (a["Activity Name"] || a.name || "").toLowerCase();
                                const code = (a["Activity Code"] || a.code || "").toLowerCase();
                                const query = activitySearch.toLowerCase();
                                return name.includes(query) || code.includes(query);
                              })
                              .slice(0, 20)
                              .map((activity, idx) => {
                                const alreadyAdded = editedActivities.some(
                                  (a) =>
                                    (a["Activity Code"] || a.code) ===
                                    (activity["Activity Code"] || activity.code)
                                );
                                return (
                                  <button
                                    key={idx}
                                    onClick={() => !alreadyAdded && handleAddActivity(activity)}
                                    disabled={alreadyAdded}
                                    className={`w-full text-left px-4 py-2.5 border-b border-gray-100 last:border-b-0 ${
                                      alreadyAdded
                                        ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                                        : "hover:bg-indigo-50 cursor-pointer"
                                    }`}
                                  >
                                    <p className="text-sm font-medium">
                                      {activity["Activity Name"] || activity.name}
                                      {alreadyAdded && (
                                        <span className="ml-2 text-xs text-gray-400">(added)</span>
                                      )}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {[
                                        activity["Activity Code"] || activity.code
                                          ? `Code: ${activity["Activity Code"] || activity.code}`
                                          : null,
                                        activity.Classification || activity.classification,
                                      ]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    </p>
                                  </button>
                                );
                              })}
                            {availableActivities.filter((a) => {
                              const name = (a["Activity Name"] || a.name || "").toLowerCase();
                              const code = (a["Activity Code"] || a.code || "").toLowerCase();
                              const query = activitySearch.toLowerCase();
                              return name.includes(query) || code.includes(query);
                            }).length === 0 && (
                              <p className="px-4 py-3 text-sm text-gray-500">
                                {availableActivities.length === 0
                                  ? "Loading activities..."
                                  : "No matching activities found"}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Save / Cancel */}
                      <div className="flex justify-end space-x-3 pt-2">
                        <button
                          onClick={() => setEditingActivities(false)}
                          disabled={savingActivities}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveActivities}
                          disabled={savingActivities}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400"
                        >
                          {savingActivities ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              Save Activities
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {company.companyActivities.map((activity, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-4 border rounded-lg"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900">
                              {activity["Activity Name"] || activity.name}
                            </p>
                            <p className="text-sm text-gray-500 mt-0.5">
                              {[
                                activity["Activity Code"] || activity.code
                                  ? `Code: ${activity["Activity Code"] || activity.code}`
                                  : null,
                                activity.Classification || activity.classification,
                                activity["Approving Entity 1"],
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          </div>
                          {(activity.Type || activity.type) && (
                            <span className="ml-3 flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {activity.Type || activity.type}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Package & Pricing */}
            {pkg && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="border-b border-gray-200 px-6 py-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Package & Pricing
                  </h3>
                </div>
                <div className="px-6 py-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        License Duration
                      </p>
                      <p className="text-sm text-gray-900">
                        {pkg.isMultiYear
                          ? `${pkg.multiyear} Years`
                          : "1 Year"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        License Fee
                      </p>
                      <p className="text-sm text-gray-900">
                        {formatLicenseFee(pkg.licenseFee, pkg.multiyear)}
                      </p>
                    </div>
                    {(pkg.discountedFee || pkg.discountFee) && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Discounted Fee
                        </p>
                        <p className="text-sm text-green-700 font-medium">
                          {formatLicenseFee(
                            pkg.discountedFee || pkg.discountFee,
                            pkg.multiyear
                          )}
                        </p>
                      </div>
                    )}
                    {pkg.establishmentCard != null && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Establishment Card
                        </p>
                        <p className="text-sm text-gray-900">
                          AED {Number(pkg.establishmentCard).toLocaleString()}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Visas Included
                      </p>
                      <p className="text-sm text-gray-900">{pkg.visa ?? 0}</p>
                    </div>
                    {pkg.activityCount != null && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Activities Included
                        </p>
                        <p className="text-sm text-gray-900">{pkg.activityCount}</p>
                      </div>
                    )}
                    {pkg.additionalActivityCost > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Additional Activity Cost
                        </p>
                        <p className="text-sm text-gray-900">
                          AED {Number(pkg.additionalActivityCost).toLocaleString()}
                        </p>
                      </div>
                    )}
                    {company?.paymentStatus && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Payment Status
                        </p>
                        <span
                          className={`inline-flex mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            company.paymentStatus === "complete"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {company.paymentStatus}
                        </span>
                      </div>
                    )}
                    {company?.discountCode && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Discount Code
                        </p>
                        <p className="text-sm text-gray-900 font-mono">
                          {company.discountCode}
                        </p>
                      </div>
                    )}
                    {company?.invoiceId && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Invoice ID
                        </p>
                        <p className="text-sm text-gray-900 font-mono break-all">
                          {company.invoiceId}
                        </p>
                      </div>
                    )}
                    {company?.checkoutSessionId && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Stripe Session
                        </p>
                        <p className="text-sm text-gray-900 font-mono text-xs break-all">
                          {company.checkoutSessionId}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Add-ons */}
                  {company?.addons &&
                    Object.entries(company.addons).some(
                      ([, addon]) => addon.selected
                    ) && (
                      <div className="mt-6 border-t pt-6">
                        <h4 className="text-base font-medium text-gray-900 mb-4">
                          Add-ons
                        </h4>
                        <div className="space-y-3">
                          {Object.entries(company.addons).map(
                            ([key, addon]) =>
                              addon.selected && (
                                <div
                                  key={key}
                                  className="flex justify-between items-start p-4 border rounded-lg"
                                >
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {(addon.package || key)
                                        .replace(/([A-Z])/g, " $1")
                                        .replace(/^./, (str) =>
                                          str.toUpperCase()
                                        )}
                                    </p>
                                    {addon.mainService && (
                                      <p className="text-sm text-gray-500 mt-0.5">
                                        {addon.mainService
                                          .replace(/([A-Z])/g, " $1")
                                          .replace(/^./, (str) =>
                                            str.toUpperCase()
                                          )}
                                      </p>
                                    )}
                                    {addon.timeline && (
                                      <p className="text-sm text-gray-500">
                                        Timeline: {addon.timeline}
                                      </p>
                                    )}
                                  </div>
                                  <div className="text-right flex-shrink-0 ml-4">
                                    <p className="font-medium text-gray-900">
                                      AED {addon.cost?.toLocaleString()}
                                    </p>
                                    {addon.quantity > 1 && (
                                      <p className="text-sm text-gray-500">
                                        Qty: {addon.quantity}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* Share Capital & Management */}
            {shDetails && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="border-b border-gray-200 px-6 py-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Share Capital & Management
                  </h3>
                </div>
                <div className="px-6 py-4 space-y-8">
                  {/* Share Capital */}
                  {shDetails.shareCapital && (
                    <div>
                      <h4 className="text-base font-medium text-gray-900 mb-4">
                        Share Capital
                      </h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-500">
                            Total Value
                          </p>
                          <p className="text-sm text-gray-900">
                            AED{" "}
                            {shDetails.shareCapital.totalValue?.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">
                            Number of Shares
                          </p>
                          <p className="text-sm text-gray-900">
                            {shDetails.shareCapital.numberOfShares?.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">
                            Value per Share
                          </p>
                          <p className="text-sm text-gray-900">
                            AED{" "}
                            {shDetails.shareCapital.valuePerShare?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Shareholders */}
                  {shDetails.shareholders?.length > 0 && (
                    <div>
                      <h4 className="text-base font-medium text-gray-900 mb-4">
                        Shareholders
                      </h4>
                      <div className="space-y-4">
                        {shDetails.shareholders.map((sh, index) => (
                          <div key={index}>
                            {renderPersonCard(sh, {
                              showShares: true,
                              showKYC: true,
                              showResidency: true,
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Company Secretary */}
                  {shDetails.secretary && (
                    <div>
                      <h4 className="text-base font-medium text-gray-900 mb-4">
                        Company Secretary
                      </h4>
                      {renderPersonCard(shDetails.secretary)}
                    </div>
                  )}

                  {/* General Manager */}
                  {shDetails.generalManager && (
                    <div>
                      <h4 className="text-base font-medium text-gray-900 mb-4">
                        General Manager
                      </h4>
                      {renderPersonCard(shDetails.generalManager)}
                    </div>
                  )}

                  {/* Directors */}
                  {shDetails.directors?.length > 0 && (
                    <div>
                      <h4 className="text-base font-medium text-gray-900 mb-4">
                        Directors
                      </h4>
                      <div className="space-y-4">
                        {shDetails.directors.map((director, index) => {
                          // Directors can be full objects or just shareholder IDs
                          if (typeof director === "object" && director !== null && director.name) {
                            return (
                              <div key={index}>
                                {renderPersonCard(director)}
                              </div>
                            );
                          }
                          // Resolve ID to shareholder
                          const resolved = shDetails.shareholders?.find(
                            (sh) => sh.id === director || String(sh.id) === String(director)
                          );
                          if (resolved) {
                            return (
                              <div key={index} className="p-4 border rounded-lg">
                                <p className="font-medium text-gray-900">{resolved.name}</p>
                                <p className="text-sm text-gray-500">
                                  {resolved.nationality} · {resolved.email}
                                </p>
                              </div>
                            );
                          }
                          return (
                            <div key={index} className="p-4 border rounded-lg">
                              <p className="text-sm text-gray-500">Director ID: {String(director)}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Ultimate Beneficial Owner */}
            {company?.ubo && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="border-b border-gray-200 px-6 py-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Ultimate Beneficial Owner (UBO)
                  </h3>
                </div>
                <div className="px-6 py-4">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        UBO Option
                      </p>
                      <p className="text-sm text-gray-900 mt-1 capitalize">
                        {company.ubo.uboOption === "allShareholders"
                          ? "All Shareholders"
                          : company.ubo.uboOption === "specificMembers"
                          ? "Specific Members"
                          : company.ubo.uboOption}
                      </p>
                    </div>

                    {company.ubo.uboMembersList?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-500 mb-3">
                          UBO Members
                        </p>
                        <div className="space-y-3">
                          {company.ubo.uboMembersList.map((member, index) => (
                            <div key={index} className="p-4 border rounded-lg">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                  <p className="text-sm text-gray-500">Name</p>
                                  <p className="text-sm font-medium text-gray-900">
                                    {member.name}
                                  </p>
                                </div>
                                {member.nationality && (
                                  <div>
                                    <p className="text-sm text-gray-500">
                                      Nationality
                                    </p>
                                    <p className="text-sm text-gray-900">
                                      {member.nationality}
                                    </p>
                                  </div>
                                )}
                                {member.shares != null && (
                                  <div>
                                    <p className="text-sm text-gray-500">
                                      Ownership
                                    </p>
                                    <p className="text-sm text-gray-900">
                                      {member.shares}%
                                    </p>
                                  </div>
                                )}
                                {member.percentage != null && !member.shares && (
                                  <div>
                                    <p className="text-sm text-gray-500">
                                      Ownership
                                    </p>
                                    <p className="text-sm text-gray-900">
                                      {member.percentage}%
                                    </p>
                                  </div>
                                )}
                                {member.email && (
                                  <div>
                                    <p className="text-sm text-gray-500">Email</p>
                                    <p className="text-sm text-gray-900 break-all">
                                      {member.email}
                                    </p>
                                  </div>
                                )}
                                {(member.phone || member.mobile) && (
                                  <div>
                                    <p className="text-sm text-gray-500">Phone</p>
                                    <p className="text-sm text-gray-900">
                                      {member.phone || member.mobile}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Application Progress */}
            <ApplicationSteps
              company={company}
              onStepUpdate={async (stepKey, status) => {
                try {
                  await handleStepUpdate(stepKey, status);
                  await fetchCompany();
                } catch (error) {
                  console.error("Error updating step:", error);
                  setNotification({
                    show: true,
                    message: "Failed to update step",
                    type: "error",
                  });
                }
              }}
            />
          </>
        )}
      </div>
    );
  };

  // Service type specific components
  const _ServiceDetails = ({ service }) => {
    const renderServiceSpecificDetails = () => {
      switch (service.serviceType) {
        case "Company Stamp":
          return (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Emirate:</span>
                <span className="ml-2 text-gray-900">{service.emirate}</span>
              </div>
              {service.deliveryMethod === "delivery" && (
                <div>
                  <span className="text-gray-500">Delivery Address:</span>
                  <p className="mt-1 text-gray-900 whitespace-pre-line">
                    {service.deliveryAddress}
                  </p>
                </div>
              )}
              {service.deliveryMethod === "pickup" &&
                service.pickupLocation && (
                  <div>
                    <span className="text-gray-500">Pickup Location:</span>
                    <p className="mt-1 text-gray-900">
                      {service.pickupLocation}
                    </p>
                  </div>
                )}
            </div>
          );

        case "Corporate Tax":
          return (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Revenue Threshold:</span>
                <span className="ml-2 text-gray-900">
                  {service.revenueThreshold === "true" ? "Yes" : "No"}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Has Residence Visa:</span>
                <span className="ml-2 text-gray-900">
                  {service.hasResidenceVisa === "true" ? "Yes" : "No"}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Exclusions:</span>
                <span className="ml-2 text-gray-900">
                  {service.exclusions === "true" ? "Yes" : "No"}
                </span>
              </div>
            </div>
          );

        case "Bank Account Opening":
          return (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Bank Plan:</span>
                  <span className="ml-2 text-gray-900">{service.bankPlan}</span>
                </div>
                <div>
                  <span className="text-gray-500">Account Type:</span>
                  <span className="ml-2 text-gray-900">
                    {service.accountType}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">PO Box:</span>
                  <span className="ml-2 text-gray-900">{service.poBox}</span>
                </div>
              </div>

              {service.shareholders && service.shareholders.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Shareholders
                  </h4>
                  <div className="space-y-4">
                    {service.shareholders.map((shareholder, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Name:</span>
                            <span className="ml-2 text-gray-900">
                              {shareholder.name}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Email:</span>
                            <span className="ml-2 text-gray-900">
                              {shareholder.email}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Mobile:</span>
                            <span className="ml-2 text-gray-900">
                              {shareholder.mobile}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Address:</span>
                            <p className="mt-1 text-gray-900">
                              {shareholder.address}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );

        default:
          return null;
      }
    };

    return (
      <div className="space-y-4">
        {renderServiceSpecificDetails()}

        {/* Documents Section */}
        {service.documents && Object.keys(service.documents).length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              Documents
            </h4>
            <div className="grid grid-cols-1 gap-2">
              {Object.entries(service.documents).map(([key, url]) => {
                // Clean up document key names
                const docName = key
                  .replace(/\[\d+\]/g, "") // Remove array indices
                  .replace(/\[|\]/g, "") // Remove remaining brackets
                  .split(/(?=[A-Z])/)
                  .join(" ") // Split on capital letters
                  .replace(/^\w/, (c) => c.toUpperCase()); // Capitalize first letter

                return (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center p-2 hover:bg-gray-50 rounded-md group"
                  >
                    <FileText className="h-4 w-4 text-gray-400 group-hover:text-gray-500" />
                    <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-700">
                      {docName}
                    </span>
                    <Download className="h-4 w-4 ml-auto text-gray-400 group-hover:text-gray-500" />
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderServicesSection = () => {
    const getServiceIcon = (serviceType) => {
      switch (serviceType) {
        case "Company Stamp":
          return Stamp;
        case "Corporate Tax":
          return Receipt;
        case "Bank Account Opening":
          return Building;
        default:
          return BriefcaseIcon;
      }
    };

    if (servicesLoading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Services</h3>
            <p className="mt-1 text-sm text-gray-500">
              Manage and track your business services
            </p>
          </div>
        </div>

        {/* Services List */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          {!services?.length ? (
            <div className="text-center py-16 px-4">
              <div className="mx-auto h-12 w-12 text-gray-400 bg-gray-50 rounded-lg flex items-center justify-center">
                <BriefcaseIcon className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-sm font-medium text-gray-900">
                No services
              </h3>
              <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
                No services have been requested yet. Services will appear here
                once requested.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {services.map((service, index) => {
                const ServiceIcon = getServiceIcon(service.serviceType);

                return (
                  <li
                    key={index}
                    onClick={() =>
                      navigate(`/companies/${id}/services/${service._id}`)
                    }
                    className="group relative hover:bg-gray-50 transition-all duration-200 cursor-pointer"
                  >
                    <div className="p-6">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="h-12 w-12 rounded-lg bg-indigo-50 group-hover:bg-indigo-100 transition-colors flex items-center justify-center">
                            <ServiceIcon className="h-6 w-6 text-indigo-600" />
                          </div>
                        </div>
                        <div className="ml-4 flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-base font-medium text-gray-900 group-hover:text-indigo-600 transition-colors">
                                {service.serviceType}
                              </h4>
                              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500">
                                <div className="flex items-center">
                                  <Calendar className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                  {new Date(
                                    service.requestDate
                                  ).toLocaleDateString()}
                                </div>

                                {/* Service specific preview info */}
                                {service.serviceType === "Company Stamp" && (
                                  <div className="flex items-center">
                                    <span className="mx-2 text-gray-300">
                                      •
                                    </span>
                                    <MapPin className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                    {service.emirate}
                                  </div>
                                )}

                                {service.serviceType ===
                                  "Bank Account Opening" && (
                                  <div className="flex items-center">
                                    <span className="mx-2 text-gray-300">
                                      •
                                    </span>
                                    <Building className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                    {service.bankPlan} Plan
                                  </div>
                                )}

                                {/* Document count */}
                                {service.documents &&
                                  Object.keys(service.documents).length > 0 && (
                                    <div className="flex items-center">
                                      <span className="mx-2 text-gray-300">
                                        •
                                      </span>
                                      <FileText className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                      {Object.keys(service.documents).length}{" "}
                                      Documents
                                    </div>
                                  )}
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getStatusColor(
                                  service.status
                                )}`}
                              >
                                {service.status}
                              </span>
                              {service.paymentStatus && (
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${
                                    service.paymentStatus === "complete"
                                      ? "bg-green-100 text-green-800"
                                      : "bg-yellow-100 text-yellow-800"
                                  }`}
                                >
                                  {service.paymentStatus}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="ml-4">
                          <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-indigo-500 transition-colors" />
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return renderOverviewContent();

      case "documents":
        return renderDocumentsSection();

      case "services":
        return renderServicesSection();
      case "activity":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">
              Activity History
            </h3>
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="space-y-6">
                {company?.applicationSteps?.map(
                  (step, index) =>
                    step.completedAt && (
                      <ActivityItem
                        key={index}
                        icon={
                          step.status === "approved"
                            ? CheckCircle2
                            : AlertCircle
                        }
                        title={`${step.name} ${
                          step.status === "approved" ? "Completed" : "Updated"
                        }`}
                        description={step.description}
                        date={step.completedAt}
                      />
                    )
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };
  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={() => navigate("/companies")}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Companies
          </button>
          <div className="flex space-x-4">
            <button
              onClick={() => navigate(`/companies/${id}/edit`)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </button>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {company?.companyNames?.first?.english}
                  </h1>
                  <p className="text-sm text-gray-500">
                    {safeStr(company?.companyDetails?.typeOfCompany)} •{" "}
                    {safeStr(company?.companyDetails?.location)}
                  </p>
                </div>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                  company?.status
                )}`}
              >
                {company?.status}
              </span>
            </div>
          </div>

          <div className="border-t border-gray-200">
            <div className="px-6 py-4 flex space-x-4 overflow-x-auto">
              <TabButton tab="overview" icon={Building2} label="Overview" />
              <TabButton tab="services" icon={BriefcaseIcon} label="Services" />
              <TabButton
                tab="documents"
                icon={FileText}
                label="Documents"
                count={company ? normalizeDocuments(company).length : 0}
              />
              {/* <TabButton tab="tasks" icon={Table} label="Tasks" /> */}
            </div>
          </div>
          <div className="px-6 py-6">{renderTabContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailView;
